import React from 'react';
import { View, Text, StyleSheet, TouchableOpacity } from 'react-native';
import Icon from 'react-native-vector-icons/MaterialIcons';
import { useDispatch, useSelector } from 'react-redux';
import type { AppDispatch, RootState } from '../../../store';
import { setTutorPersonality } from '../../../store/slices/aiTutoringSlice';

type TutorPersonality = 'encouraging' | 'professional' | 'friendly' | 'expert';

const TutorPersonalitySelector: React.FC = () => {
  const dispatch = useDispatch<AppDispatch>();
  const currentPersonality = useSelector(
    (state: RootState) => state.aiTutoring.tutorPersonality
  );

  const personalities: Array<{
    key: TutorPersonality;
    name: string;
    description: string;
    icon: string;
    color: string;
    traits: string[];
  }> = [
    {
      key: 'encouraging',
      name: 'Encouraging',
      description: 'Supportive and motivational approach',
      icon: 'sentiment-very-satisfied',
      color: '#4CAF50',
      traits: [
        'Positive reinforcement',
        'Celebrates progress',
        'Builds confidence',
      ],
    },
    {
      key: 'professional',
      name: 'Professional',
      description: 'Structured and formal teaching style',
      icon: 'business-center',
      color: '#2196F3',
      traits: ['Clear structure', 'Detailed explanations', 'Goal-oriented'],
    },
    {
      key: 'friendly',
      name: 'Friendly',
      description: 'Casual and conversational tone',
      icon: 'emoji-people',
      color: '#FF9800',
      traits: ['Conversational', 'Relatable examples', 'Approachable'],
    },
    {
      key: 'expert',
      name: 'Expert',
      description: 'Technical and comprehensive guidance',
      icon: 'psychology',
      color: '#9C27B0',
      traits: ['Deep insights', 'Technical accuracy', 'Advanced concepts'],
    },
  ];

  const handlePersonalityChange = (personality: TutorPersonality) => {
    dispatch(setTutorPersonality(personality));
  };

  const renderPersonalityCard = (personality: (typeof personalities)[0]) => {
    const isSelected = currentPersonality === personality.key;

    return (
      <TouchableOpacity
        key={personality.key}
        style={[
          styles.personalityCard,
          isSelected && {
            backgroundColor: `${personality.color}15`,
            borderColor: personality.color,
          },
        ]}
        onPress={() => handlePersonalityChange(personality.key)}
        activeOpacity={0.7}
      >
        <View style={styles.personalityHeader}>
          <View
            style={[
              styles.personalityIcon,
              { backgroundColor: `${personality.color}20` },
            ]}
          >
            <Icon name={personality.icon} size={20} color={personality.color} />
          </View>
          <View style={styles.personalityInfo}>
            <Text
              style={[
                styles.personalityName,
                isSelected && { color: personality.color },
              ]}
            >
              {personality.name}
            </Text>
            <Text style={styles.personalityDescription}>
              {personality.description}
            </Text>
          </View>
          {isSelected && (
            <Icon name='check-circle' size={20} color={personality.color} />
          )}
        </View>

        <View style={styles.traitsContainer}>
          {personality.traits.map((trait, index) => (
            <View key={index} style={styles.traitItem}>
              <View
                style={[
                  styles.traitDot,
                  { backgroundColor: personality.color },
                ]}
              />
              <Text style={styles.traitText}>{trait}</Text>
            </View>
          ))}
        </View>
      </TouchableOpacity>
    );
  };

  return (
    <View style={styles.container}>
      <View style={styles.header}>
        <Icon name='psychology' size={20} color='#007AFF' />
        <Text style={styles.title}>AI Tutor Personality</Text>
      </View>
      <Text style={styles.subtitle}>
        Choose how your AI tutor communicates and teaches
      </Text>

      <View style={styles.personalitiesGrid}>
        {personalities.map(renderPersonalityCard)}
      </View>

      <View style={styles.previewSection}>
        <Text style={styles.previewTitle}>Preview</Text>
        <View style={styles.previewCard}>
          <Text style={styles.previewLabel}>Sample explanation:</Text>
          <Text style={styles.previewText}>
            {getPreviewText(currentPersonality)}
          </Text>
        </View>
      </View>
    </View>
  );
};

const getPreviewText = (personality: TutorPersonality): string => {
  const previews = {
    encouraging:
      "Great job exploring coffee brewing! Let's dive into grind size - you're going to love how this simple change can transform your cup. Every small step you take is building your expertise!",
    professional:
      'We will now examine grind size optimization. Grind size directly correlates with extraction rate and flavor profile. Understanding this relationship is fundamental to achieving consistent brewing results.',
    friendly:
      "Hey there! So you want to know about grind size? Think of it like this - it's basically how fine or coarse your coffee grounds are, and it makes a huge difference in how your coffee tastes!",
    expert:
      'Grind size represents particle distribution affecting extraction kinetics. Finer grinds increase surface area, accelerating soluble compound extraction but risking over-extraction. Optimal particle size depends on brewing method, contact time, and desired flavor compounds.',
  };

  return previews[personality];
};

const styles = StyleSheet.create({
  container: {
    backgroundColor: '#fff',
  },
  header: {
    flexDirection: 'row',
    alignItems: 'center',
    marginBottom: 8,
    gap: 8,
  },
  title: {
    fontSize: 16,
    fontWeight: '600',
    color: '#1a1a1a',
  },
  subtitle: {
    fontSize: 12,
    color: '#666',
    marginBottom: 16,
  },
  personalitiesGrid: {
    gap: 12,
    marginBottom: 20,
  },
  personalityCard: {
    backgroundColor: '#f8f9fa',
    borderRadius: 8,
    padding: 12,
    borderWidth: 2,
    borderColor: 'transparent',
  },
  personalityHeader: {
    flexDirection: 'row',
    alignItems: 'center',
    marginBottom: 8,
  },
  personalityIcon: {
    width: 32,
    height: 32,
    borderRadius: 16,
    alignItems: 'center',
    justifyContent: 'center',
    marginRight: 10,
  },
  personalityInfo: {
    flex: 1,
  },
  personalityName: {
    fontSize: 14,
    fontWeight: '600',
    color: '#1a1a1a',
    marginBottom: 2,
  },
  personalityDescription: {
    fontSize: 12,
    color: '#666',
  },
  traitsContainer: {
    gap: 4,
  },
  traitItem: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 6,
  },
  traitDot: {
    width: 4,
    height: 4,
    borderRadius: 2,
  },
  traitText: {
    fontSize: 11,
    color: '#666',
  },
  previewSection: {
    borderTopWidth: 1,
    borderTopColor: '#e1e5e9',
    paddingTop: 16,
  },
  previewTitle: {
    fontSize: 14,
    fontWeight: '600',
    color: '#1a1a1a',
    marginBottom: 8,
  },
  previewCard: {
    backgroundColor: '#f8f9fa',
    borderRadius: 8,
    padding: 12,
  },
  previewLabel: {
    fontSize: 12,
    fontWeight: '500',
    color: '#666',
    marginBottom: 6,
  },
  previewText: {
    fontSize: 13,
    color: '#1a1a1a',
    lineHeight: 18,
    fontStyle: 'italic',
  },
});

export default TutorPersonalitySelector;
